import { connect } from 'react-redux';

import {
  OBJECT_TYPE_ASSETS,
  OBJECT_TYPE_COLLECTIONS,
} from '@iconik-io/iconik-api/src/types/object_types';
import { createEntityDenormalizer } from '@iconik-io/shared-lib/src/redux/entities/normalizers';
import { selectFilesByAssetId } from '@iconik-io/shared-lib/src/redux/entities/selectors/files';
import { selectFormatsByAssetId } from '@iconik-io/shared-lib/src/redux/entities/selectors/formats';
import { selectLatestActiveVersionId } from '@iconik-io/shared-lib/src/redux/entities/selectors/versions';

import { selectPermissions } from '../../components/permissions_manager/selectors';
import {
  ASSET_METADATA_KEY,
  SEARCH_ASSET_METADATA_PANEL,
} from '../../constants/data';
import {
  ASSET_PAGE_ROUTE,
  ASSET_VERSION_PAGE_ROUTE,
  COLLECTION_PAGE_ROUTE,
  SAVED_SEARCH_PAGE_ROUTE,
  SEARCH_PAGE_ROUTE,
  SHARE_ASSET_PAGE_ROUTE,
  SHARE_ASSET_VERSION_PAGE_ROUTE,
  SHARE_COLLECTION_PAGE_ROUTE,
} from '../../constants/routes';
import { segmentTrackEvent } from '../../ducks/analytics_tracking/thunks';
import { setGettingStartedCheck } from '../../ducks/app/slice';
import { getSelectedItemIds } from '../../ducks/asset/selectors';
import { selectCollectionAsSearchItemEntity } from '../../ducks/collections/selectors';
import {
  metadataValuesAddAwaiting,
  metadataValuesClearById,
} from '../../ducks/metadata_values/slice';
import {
  getMetadataValues,
  putMetadataValues,
} from '../../ducks/metadata_values/thunks';
import { selectAsset } from '../../ducks/pages/asset/selectors';
import {
  setPanelDirtyById,
  setPanelPristineById,
} from '../../ducks/panels/pristine/slice';
import { combineSearchItemFromAsset } from '../../ducks/search/selectors';
import { metadataEntitySelectView } from '../../ducks/selected_metadata/slice';
import type { AppState } from '../../store/types';
import type { PermissionValues } from '../../types/common';
import { getRegularObjectLink } from '../../utils/get_object_link';
import { selectUserRoles } from '../../utils/get_user_roles';

type Props = {
  entityPermissions?: PermissionValues;
};

const metadataWithPodRoutes = new Set([
  SEARCH_PAGE_ROUTE,
  COLLECTION_PAGE_ROUTE,
  SAVED_SEARCH_PAGE_ROUTE,
]);

const denormalizeLastSelectedSearchItem =
  createEntityDenormalizer('search_items');

const mapStateToProps = (state: AppState, ownProps: Props) => {
  const { route, params } = state.routeParams;

  let entity;
  let item;
  let link;
  let openedVersionId;
  let isLatestVersion;

  const selectedIds = getSelectedItemIds(state);
  const lastSelectedId = selectedIds?.at(-1);

  switch (route) {
    case SEARCH_PAGE_ROUTE:
    case SAVED_SEARCH_PAGE_ROUTE: {
      if (lastSelectedId) {
        item = denormalizeLastSelectedSearchItem(
          state.entities,
          lastSelectedId,
        );
        entity = item?.object_type;
      }
      break;
    }
    case SHARE_COLLECTION_PAGE_ROUTE:
    case COLLECTION_PAGE_ROUTE: {
      if (lastSelectedId) {
        item = denormalizeLastSelectedSearchItem(
          state.entities,
          lastSelectedId,
        );
        entity = item?.object_type;
      } else {
        item = selectCollectionAsSearchItemEntity(
          state,
          params.id,
          route === SHARE_COLLECTION_PAGE_ROUTE,
        );
        entity = OBJECT_TYPE_COLLECTIONS;
      }
      break;
    }
    case ASSET_PAGE_ROUTE:
    case ASSET_VERSION_PAGE_ROUTE:
    case SHARE_ASSET_PAGE_ROUTE:
    case SHARE_ASSET_VERSION_PAGE_ROUTE: {
      const itemId = params.id;
      const asset = selectAsset(state, itemId);
      const formats = selectFormatsByAssetId(state, itemId);
      const files = selectFilesByAssetId(state, itemId);
      item = combineSearchItemFromAsset(asset, formats, files);
      entity = OBJECT_TYPE_ASSETS;
      break;
    }
    default:
      break;
  }

  if (entity === OBJECT_TYPE_ASSETS) {
    let latestActiveVersionId;
    if (item && 'versions' in item) {
      latestActiveVersionId = selectLatestActiveVersionId(state, item.id);
    }
    openedVersionId = params.versionId ?? latestActiveVersionId;
    isLatestVersion = openedVersionId === latestActiveVersionId;
  }

  const withPreview =
    (metadataWithPodRoutes.has(route) && lastSelectedId) ||
    route === COLLECTION_PAGE_ROUTE;

  if (withPreview && lastSelectedId && item && entity) {
    link = getRegularObjectLink(item.id, entity);
  }

  // for asset and collection we need to select permissions
  // for shares permissions are coming from the top
  let entityPermissions = ownProps.entityPermissions;
  if (!entityPermissions && entity) {
    entityPermissions = item
      ? selectPermissions(state, entity, item.id)
      : undefined;
  }

  return {
    metadataValues: state.metadataValuesReducer.metadataValues,
    metadataViews: state.entities.metadata_views,
    awaitingMetadataValues: state.metadataValuesReducer.awaitingMetadataValues,
    breakpoint: state.displayReducer.breakpoint,
    entityPermissions,
    route,
    roles: selectUserRoles(state),
    selectedMetadataViewId: state.panelsState.metadata.entities,
    panelsPristine: state.panelsPristine,
    item,
    openedVersionId,
    isLatestVersion,
    withPreview,
    link,
    metadataKey: withPreview ? SEARCH_ASSET_METADATA_PANEL : ASSET_METADATA_KEY,
    hasMultipleItemsSelected: selectedIds ? selectedIds.length > 1 : false,
  };
};

const connector = connect(mapStateToProps, {
  getMetadataValues,
  putMetadataValues,
  metadataValuesAddAwaiting,
  setGettingStartedCheck,
  segmentTrackEvent,
  metadataValuesClearById,
  metadataEntitySelectView,
  setPanelPristineById,
  setPanelDirtyById,
});

export default connector;
